import { useState, useEffect, useCallback, useRef } from "react"
import { Link, useLocation, useHistory } from "react-router-dom"
import {
  Navbar,
  Nav,
  Dropdown,
  NavDropdown,
} from "react-bootstrap"

import "react-bootstrap-typeahead/css/Typeahead.css"
import OrganizationBox from "../Settings/OrganizationBox"
import Conversations from "../Chat/Conversations"
import LanguageToggle from "./LanguageToggle"

import API from "../../common/API"
import { Routes } from "../../routes"
import { useTranslation } from "react-i18next"
import settings from "../../common/Settings"
import { parsedUser } from "@/common/GetCurrentUser"
import { isSuperAdmin } from "@/common/helpers"
import { createRealtimeConnection, startConnectionSafe, stopConnectionSafe } from "@/services/realtime"
import { useToast } from "./Toast"

export default function ResponsiveNavbar(): JSX.Element {
  const { t } = useTranslation("global");
  const { info } = useToast()
  const user: any = parsedUser();
  const location = useLocation()
  const history = useHistory()
  const [expanded, setExpanded] = useState(false)

  const [chatCount, setChatCount] = useState(0)
  const [favoritesCount, setFavoritesCount] = useState(0)
  const [showChat, setShowChat] = useState(false)
  const navRef = useRef<HTMLDivElement | null>(null)
  const prevChatCountRef = useRef<number>(0)
  const unreadInitializedRef = useRef<boolean>(false)

  const toggleSearch = useCallback(() => {
    history.push("/search")
  }, [history])

  const toggleFavorites = useCallback(() => {
    history.push(Routes.Favorites.path)
    setExpanded(false)
  }, [history])
  const handleToggle = useCallback(() => {
    setExpanded(prev => !prev)
  }, [])

  // Close navbar when clicking on nav items
  const handleNavClick = useCallback(() => {
    setExpanded(false)
  }, [])

  // Close navbar on outside click or escape key
  useEffect(() => {
    const handleEscape = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape" && expanded) {
        handleNavClick()
      }
    }

    const handleClickOutside = (e: MouseEvent) => {
      if (!expanded) return
      const target = e.target as Node
      if (navRef.current && !navRef.current.contains(target)) {
        handleNavClick()
      }
    }

    document.addEventListener("keydown", handleEscape)
    document.addEventListener("click", handleClickOutside)

    return () => {
      document.removeEventListener("keydown", handleEscape)
      document.removeEventListener("click", handleClickOutside)
    }
  }, [expanded, handleNavClick])

  // Prevent background scroll when navbar is expanded on mobile
  useEffect(() => {
    if (expanded) document.body.classList.add("navbar-expanded")
    else document.body.classList.remove("navbar-expanded")

    return () => {
      document.body.classList.remove("navbar-expanded")
    }
  }, [expanded])

  const toggleChat = useCallback(() => {
    setShowChat(prev => !prev)
  }, [])

  const requestChatCount = useCallback(async () => {
    if (!user?.ID) return
    try {
      const response = await API.getAction(
        "Chat/UnreadCount",
        "userId=" + user.ID
      )
      if (response.data.status === "ok") {
        const unreadCount = Number(response.data.response) || 0
        setChatCount(unreadCount)
      }
    } catch (error) {
      console.error("Error fetching chat count:", error)
    }
  }, [user.ID])

  const requestFavoritesCount = useCallback(async () => {
    if (!user?.ID) return
    try {
      const response = await API.getAction("place/priorityList", `accountId=${user.ID}`)
      if (response.data.status === "ok") {
        const count = Array.isArray(response.data.response) ? response.data.response.length : 0
        setFavoritesCount(count)
      }
    } catch (error) {
      console.error("Error fetching favorites count:", error)
    }
  }, [user.ID])

  const refreshCounters = useCallback(() => {
    requestChatCount()
    requestFavoritesCount()
  }, [requestChatCount, requestFavoritesCount])

  useEffect(() => {
    refreshCounters()
    const interval = setInterval(refreshCounters, 30000)
    return () => clearInterval(interval)
  }, [refreshCounters])

  useEffect(() => {
    if (!unreadInitializedRef.current) {
      unreadInitializedRef.current = true
      prevChatCountRef.current = chatCount
      return
    }

    if (chatCount > prevChatCountRef.current) {
      const delta = chatCount - prevChatCountRef.current
      const title = t("Chats")
      const message =
        delta === 1
          ? "Tienes 1 mensaje nuevo sin leer."
          : `Tienes ${delta} mensajes nuevos sin leer.`
      info(title, message, 4500)
    }

    prevChatCountRef.current = chatCount
  }, [chatCount, info, t])

  useEffect(() => {
    if (!user?.ID) return

    const connection = createRealtimeConnection()
    const refreshFromRealtime = () => {
      refreshCounters()
    }
    const registerCurrentUser = () => {
      connection.invoke("RegisterUser", String(user.ID)).catch(() => null)
    }
    const realtimeEvents = [
      "onMessage",
      "onChatMessage",
      "ReceiveMessage",
      "ReceiveChatMessage",
      "NewMessage",
      "onNotification",
      "onUnreadCountChanged",
      "UnreadCountChanged",
    ]

    realtimeEvents.forEach(eventName => connection.on(eventName, refreshFromRealtime))
    connection.onreconnected(() => {
      registerCurrentUser()
      refreshFromRealtime()
    })
    startConnectionSafe(connection).then(isConnected => {
      if (isConnected) {
        registerCurrentUser()
        refreshCounters()
      }
    })

    return () => {
      realtimeEvents.forEach(eventName => connection.off(eventName, refreshFromRealtime))
      stopConnectionSafe(connection)
    }
  }, [refreshCounters, user.ID])

  const isActiveRoute = useCallback(
    (path: string) => {
      if (path === "/") return location.pathname === "/"
      return location.pathname.startsWith(path)
    },
    [location.pathname]
  )

  const handleLogout = useCallback(() => {
    localStorage.removeItem("token")
    history.push("/login")
  }, [history])



  return (
    <>
      {showChat && (
        <Conversations
          show={showChat}
          toggle={toggleChat}
          onUnreadCountChanged={requestChatCount}
        />
      )}

      <div ref={navRef}>
        <Navbar
          bg="white"
          expand="lg"
          className="responsive-navbar shadow-sm"
          expanded={expanded}
        >
          <div className="container-fluid">
            <Navbar.Brand
              as={Link}
              to="/"
              className="fw-bold text-primary"
              onClick={handleNavClick}
            >
              <OrganizationBox readOnly />
            </Navbar.Brand>

            <Navbar.Toggle
              aria-controls="basic-navbar-nav"
              onClick={handleToggle}
              aria-expanded={expanded}
            />

            <Navbar.Collapse id="basic-navbar-nav">
              <Nav className="me-auto">
                <Nav.Link
                  as={Link}
                  title={t("Home")}
                  to="/"
                  className={`nav-button d-flex flex-column flex-lg-row align-items-center ${isActiveRoute("/") ? "active" : ""
                    }`}
                  onClick={handleNavClick}
                >
                  <i className="fas fa-home mb-1 mb-lg-0 me-xl-2" aria-hidden="true" />
                  <span className={!isActiveRoute("/") ? "d-none d-md-none d-xl-inline" : ""}>{t("Home")}</span>
                </Nav.Link>
                {isSuperAdmin(user) && (
                  <Nav.Link
                    as={Link}
                    title={t("Platform Admin")}
                    to={Routes.Platform.path}
                    className={`nav-button d-flex flex-column flex-lg-row align-items-center ${isActiveRoute(Routes.Platform.path) ? "active" : ""
                      }`}
                    onClick={handleNavClick}
                  >
                    <i className="fas fa-shield-halved mb-1 mb-lg-0 me-xl-2 text-warning" aria-hidden="true" />
                    <span className={!isActiveRoute(Routes.Platform.path) ? "d-xl-inline" : ""}>{t("Platform")}</span>
                  </Nav.Link>
                )}
                <Nav.Link
                  as={Link}
                  to="/dashboard"
                  className={`nav-button d-flex flex-column flex-lg-row align-items-center ${isActiveRoute("/dashboard") ? "active" : ""
                    }`}
                  title={t("Dashboard")}
                  onClick={handleNavClick}
                >
                  <i className="fas fa-tachometer-alt mb-1 mb-lg-0 me-xl-2" aria-hidden="true" />
                  <span className={!isActiveRoute("/dashboard") ? "d-xl-inline" : ""}>{t("Dashboard")}</span>
                </Nav.Link>

                <Nav.Link
                  as={Link}
                  title={t("Agents")}
                  to="/agents"
                  className={`nav-button d-flex flex-column flex-lg-row align-items-center ${isActiveRoute("/agents") ? "active" : ""
                    }`}
                  onClick={handleNavClick}
                >
                  <i className="fas fa-users mb-1 mb-lg-0 me-xl-2" aria-hidden="true" />
                  <span className={!isActiveRoute("/agents") ? "d-xl-inline" : ""}>{t("Agents")}</span>
                </Nav.Link>
                {/* <Nav.Link
                  onClick={() => {
                    toggleChat()
                    handleNavClick()
                  }}
                  className={`nav-button d-flex flex-column flex-lg-row align-items-center position-relative ${showChat ? "active" : ""
                    }`}
                  role="button"
                  tabIndex={0}
                  title={t("Chats")}
                  onKeyDown={(e: ReactKeyboardEvent) => {
                    if (e.key === "Enter" || e.key === " ") {
                      toggleChat()
                      handleNavClick()
                    }
                  }}
                  aria-label="Open chats"
                >
                  <i className="fas fa-comments mb-1 mb-lg-0 me-lg-2" aria-hidden />
                  <span className={!isActiveRoute("/chats") ? "d-xl-inline" : ""}>{t("Chats")}</span>
                  {chatCount > 0 && (
                    <Badge bg="danger" className="nav-badge" aria-live="polite">
                      {chatCount > 99 ? "99+" : chatCount}
                    </Badge>
                  )}
                </Nav.Link> */}
                <NavDropdown
                  title={
                    <>
                      <i className="fas fa-file-lines ms-2"></i>
                      <span className={!isActiveRoute("/dashboard") ? "d-xl-inline" : ""}>
                        {t("Reports")}
                      </span>
                    </>
                  }
                  id="nav-reports-dropdown"
                  className={isActiveRoute("/reports") ? "active" : ""}
                >
                  <NavDropdown.Item
                    as={Link}
                    to={Routes.visitReport.path}
                    onClick={handleNavClick}
                  >
                    {t("Visits")}
                  </NavDropdown.Item>

                  <NavDropdown.Item
                    as={Link}
                    to={Routes.VehicleVisits.path}
                    onClick={handleNavClick}
                  >
                    {t("Vehicle Visits")}
                  </NavDropdown.Item>
                  <NavDropdown.Item
                    as={Link}
                    to={Routes.agentVisitsReport.path}
                    onClick={handleNavClick}
                  >
                    {t("Agent Visits")}
                  </NavDropdown.Item>
                  <NavDropdown.Item
                    as={Link}
                    to="/reports/placesByAgents"
                    onClick={handleNavClick}
                  >
                    {t("Places By Agents")}
                  </NavDropdown.Item>
                  <NavDropdown.Item
                    as={Link}
                    to="/reports/newPlaces"
                    onClick={handleNavClick}
                  >
                    {t("New Places")}
                  </NavDropdown.Item>
                  {/* <NavDropdown.Item
                    as={Link}
                    to="/reports/salesByMonth"
                    onClick={handleNavClick}
                  >
                    {t("Sales By Month")}
                  </NavDropdown.Item> */}
                </NavDropdown>

                <Nav.Link
                  as={Link}
                  title={t("Analysis")}
                  to={Routes.Analysis.path}
                  className={`nav-button d-flex flex-column flex-lg-row align-items-center ${isActiveRoute(Routes.Analysis.path) ? "active" : ""}`}
                  onClick={handleNavClick}
                >
                  <i className="fas fa-chart-line mb-1 mb-lg-0 me-xl-2" aria-hidden="true" />
                  <span className={!isActiveRoute(Routes.Analysis.path) ? "d-xl-inline" : ""}>{t("Analysis")}</span>
                </Nav.Link>

                <Nav.Link
                  as={Link}
                  title={t("Incidencias")}
                  to={Routes.Incidents.path}
                  className={`nav-button d-flex flex-column flex-lg-row align-items-center ${isActiveRoute(Routes.Incidents.path) ? "active" : ""}`}
                  onClick={handleNavClick}
                >
                  <i className="fas fa-exclamation-circle mb-1 mb-lg-0 me-xl-2 text-danger" aria-hidden="true" />
                  <span className={!isActiveRoute(Routes.Incidents.path) ? "d-xl-inline" : ""}>{t("Incidencias")}</span>
                </Nav.Link>

              </Nav>

              <div className="d-flex align-items-center navbar-end">
                <button className="btn btn-light border me-2" onClick={toggleSearch} title={t("Search")}>
                  <i className="fas fa-search" aria-hidden="true"></i>
                </button>
                <button
                  className="btn btn-light border me-2 position-relative"
                  onClick={toggleFavorites}
                  title={t("Priority")}
                  aria-label={t("Priority")}
                >
                  <i className={`fas fa-star ${favoritesCount > 0 ? "text-warning" : "text-secondary"}`} aria-hidden="true"></i>
                  {favoritesCount > 0 && (
                    <span className="navbar-action-badge" aria-live="polite">
                      {favoritesCount > 99 ? "99+" : favoritesCount}
                    </span>
                  )}
                </button>
                <button
                  className="btn btn-light border me-2 position-relative"
                  onClick={toggleChat}
                  title={t("Chats")}
                  aria-label={t("Chats")}
                >
                  <i className="fas fa-comments" aria-hidden="true"></i>
                  {chatCount > 0 && (
                    <span className="navbar-action-badge navbar-action-badge-pulse" aria-live="polite">
                      {chatCount > 99 ? "99+" : chatCount}
                    </span>
                  )}
                </button>


                <Dropdown align="end">
                  <Dropdown.Toggle
                    variant="link"
                    id="dropdown-user"
                    className="nav-link p-2 border-0 bg-transparent hover-bg-light rounded"
                    style={{ boxShadow: "none" }}
                  >
                    <div className="d-flex align-items-center ">
                      <i className="fas fa-user-circle fs-4 text-dark me-2" />

                    </div>
                  </Dropdown.Toggle>

                  <Dropdown.Menu className="user-dropdown">
                    <Dropdown.Header>
                      <div className="fw-bold text-dark">
                        {user.name || user.Name || "User Name"}
                      </div>
                      <div className="text-muted small">
                        {user.email || user.Email || "user@example.com"}
                      </div>
                      <LanguageToggle />
                    </Dropdown.Header>
                    {(user.roles === settings.Roles.Admin || isSuperAdmin(user)) && (<>
                      <Dropdown.Divider />
                      <Dropdown.Item
                        onClick={() => {
                          history.push(Routes.Settings.path)
                        }}
                        className="d-flex align-items-center"
                      >
                        <i className="fas fa-user-shield me-2 " />
                        {t("Settings")}
                      </Dropdown.Item>
                    </>)}
                    {isSuperAdmin(user) && (<>
                      <Dropdown.Divider />
                      <Dropdown.Item
                        onClick={() => {
                          history.push(Routes.Platform.path)
                        }}
                        className="d-flex align-items-center"
                      >
                        <i className="fas fa-shield-halved me-2 text-warning" />
                        {t("Platform Admin")}
                      </Dropdown.Item>
                    </>)}
                    <Dropdown.Divider />
                    <Dropdown.Item
                      onClick={handleLogout}
                      className="d-flex align-items-center"
                    >
                      <i className="fas fa-sign-out-alt me-2 text-danger" />
                      {t("Sign Out")}
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </Navbar.Collapse>
          </div>
        </Navbar >
      </div >
    </>
  )
}
